<script>
import { leftArrowIcon, rightArrowIcon } from "@/utils/proposalIcons";
import draggable from "vuedraggable";

export default {
  name: "Stepper",
  components: { draggable },

  props: {
    steps: {
      type: Array,
      required: true
    },
    currentStep: {
      type: Number,
      required: true
    },
    showArrows: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      leftArrowIcon,
      rightArrowIcon,

      // local working copy
      localSteps: [],

      dragOptions: {
        animation: 150,
        handle: ".step-circle",
        filter: ".no-drag",
        preventOnFilter: true
      }
    };
  },

  created() {
    this.cloneStepsFromProps();
  },

  watch: {
    steps: {
      deep: true,
      handler() {
        this.cloneStepsFromProps();
      }
    }
  },

  computed: {
    progressWidth() {
      const total = this.steps.length;
      const completed = this.currentStep - 1;
      return `${(completed / (total - 1)) * 100}%`;
    }
  },

  methods: {
    /** Deep clone + reattach functions by stable key */
    cloneStepsFromProps() {
      const cloned = JSON.parse(JSON.stringify(this.steps));

      cloned.forEach(step => {
        const original = this.steps.find(s => s.number === step.number);
        if (original && typeof original.validate === "function") {
          step.validate = original.validate;
        }
      });

      this.localSteps = cloned;
    },

    /** Drag end handler */
    onDragEnd(evt) {
      const moved = this.localSteps[evt.newIndex];

      // If locked, revert
      if (moved.locked) {
        this.cloneStepsFromProps();
        return;
      }

      // Reattach functions before emitting
      this.localSteps.forEach(step => {
        const original = this.steps.find(s => s.number === step.number);
        if (original && typeof original.validate === "function") {
          step.validate = original.validate;
        }
      });

      this.$emit("reorder", this.localSteps);
    }
  }
};
</script>

<template>
  <div class="main-stepper-wrapper">
    <!-- Left Arrow -->
    <button
      v-if="showArrows"
      class="nav-arrow"
      :disabled="currentStep === 1"
      @click="$emit('prev')"
    >
      <span v-html="leftArrowIcon" />
    </button>

    <div class="stepper-container">
      <!-- Steps -->
      <draggable
        v-model="localSteps"
        class="stepper-wrapper"
        :options="dragOptions"
        @end="onDragEnd"
      >
        <div
          v-for="(step, i) in localSteps"
          :key="step.title+i"
          class="stepper-step"
          :class="{
            active: currentStep >= i+1,
            completed: step.validate && step.validate(),
            disabled: step.disabled,
            'no-drag': step.locked
          }"
          @click="!step.disabled && $emit('step-click', i+1)"
        >
          <div class="step-circle">
            <span class="step-number">{{ i + 1 }}</span>
          </div>
          <span class="step-label">{{ step.title||"Extra Page" }}</span>
        </div>
      </draggable>

      <!-- Progress Line -->
      <div class="stepper-line">
        <div class="stepper-progress" :style="{ width: progressWidth }"></div>
      </div>
    </div>

    <!-- Right Arrow -->
    <button
      v-if="showArrows"
      class="nav-arrow"
      :disabled="currentStep === steps.length"
      @click="$emit('next')"
    >
      <span v-html="rightArrowIcon" />
    </button>
  </div>
</template>
<style scoped lang="scss">
.main-stepper-wrapper {
  display: flex;
  align-items: center;
  width: 100%;
}

.stepper-container {
  position: relative;
  margin: 30px 20px;
  flex-grow: 1;

  @media screen and (max-width: 991px) {
    margin: 20px 10px;
  }
}

.nav-arrow {
  background: transparent;
  border: none;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  color: #6c757d;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    color: #4475b4;
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
}

.stepper-wrapper {
  display: flex;
  justify-content: space-between;
  position: relative;
  z-index: 2;
}

.stepper-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
}

.stepper-step.disabled {
  cursor: none;
  opacity: 0.6;
}

.step-circle {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: #e9ecef;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  border: 3px solid #e9ecef;
  transition: all 0.3s ease;
  position: relative;

  @media screen and (max-width: 767px) {
    width: 40px;
    height: 40px;
  }

  @media screen and (max-width: 475px) {
    width: 25px;
    height: 25px;
  }
}

.stepper-step.active .step-circle {
  background-color: #4475b4;
  border-color: #4475b4;
  transform: scale(1.1);
}

.stepper-step.completed .step-circle {
  background-color: #9cc05a;
  border-color: #9cc05a;
}

.step-number {
  color: #6c757d;
  font-weight: bold;
  font-size: 16px;

  @media screen and (max-width: 475px) {
    font-size: 12px;
  }
}

.stepper-step.active .step-number {
  color: white;
}

.stepper-step.completed .step-number {
  color: white;
}

.step-label {
  font-size: 12px;
  font-weight: 600;
  text-align: center;
  color: #6c757d;
  margin-bottom: 4px;

  @media screen and (max-width: 767px) {
    display: none;
  }
}

.stepper-step.active .step-label {
  color: #4475b4;
}

.stepper-step.completed .step-label {
  color: #9cc05a;
}

/* Connecting Line */
.stepper-line {
  position: absolute;
  top: 25px;
  left: 0px;
  right: 0;
  height: 4px;
  background-color: #e9ecef;
  z-index: 1;

  @media screen and (max-width: 767px) {
    top: 20px;
  }

  @media screen and (max-width: 475px) {
    top: 10px;
  }
}

.stepper-progress {
  height: 100%;
  background-color: #9cc05a;
  transition: width 0.3s ease;
  border-radius: 2px;
}
.stepper-step {
  cursor: move; /* default for draggable steps */
}
.no-drag {
  cursor: pointer !important;
  opacity: 0.6;
}
</style>